import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { removeAuthorization } from "../../Functions/AuthFunction";
import { useGlobalContext } from "../../ContextApi/GlobalContext";

const sidebarVariants = {
  open: { x: 0, transition: { type: "spring", stiffness: 320, damping: 32 } },
  closed: {
    x: "-105%",
    transition: { type: "spring", stiffness: 320, damping: 32 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.04, duration: 0.25 },
  }),
};

const navItems = [
  { link: "/dashboard", title: "Dashboard", icon: "dashboard" },
  { link: "/users", title: "Users", icon: "group" },
  { link: "/queries", title: "Queries", icon: "question_answer" },
  { link: "/service-leads", title: "Service Leads", icon: "handshake" },
  { link: "/bugs-report", title: "Bugs Report", icon: "bug_report" },
  { link: "/myprofile", title: "My Profile", icon: "account_circle" },
  { link: "/support", title: "Tickets", icon: "confirmation_number" },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch, state } = useGlobalContext();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const user = state.companyData || {};
  const userName = user.name || "Admin User";
  const userRole = user.role || "Administrator";
  const userInitial = userName.charAt(0).toUpperCase();
  const profilePicURL = user.profilePicURL;

  const handleListItemClick = (link, title) => {
    if (!isDesktop) {
      dispatch({ sideBar: false });
    }
    dispatch({ pageLink: link, pageName: title });
    navigate(link);
  };

  const logoutHandler = () => {
    removeAuthorization();
    dispatch({ authToken: false, companyData: false });
    navigate("/login");
  };

  const showExpanded = isDesktop || state.sideBar;

  return (
    <>
      <AnimatePresence>
        {state.sideBar && !isDesktop && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => dispatch({ sideBar: false })}
            className="fixed inset-0 bg-[#0B1C2D]/70 backdrop-blur-sm z-[999] lg:hidden"
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={false}
        animate={isDesktop ? "open" : state.sideBar ? "open" : "closed"}
        variants={sidebarVariants}
        className="fixed top-0 left-0 z-[1000] h-[100dvh] w-[min(280px,86vw)] flex flex-col bg-[#0B1C2D] border-r border-white/5 shadow-2xl lg:!translate-x-0 lg:w-[280px]"
        style={{
          paddingTop: isDesktop ? 0 : "env(safe-area-inset-top)",
          paddingBottom: isDesktop ? 0 : "env(safe-area-inset-bottom)",
        }}
      >
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
          <div className="absolute -top-[10%] -left-[10%] w-64 h-64 bg-[#E63946]/5 rounded-full blur-[80px]" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#1E3A8A]/5 rounded-full blur-[80px]" />
        </div>

        <div className="relative z-10 h-20 flex items-center justify-between px-5">
          <div
            onClick={() => handleListItemClick("/dashboard", "Dashboard")}
            className="flex items-center gap-3 group cursor-pointer min-w-0"
          >
            <div className="flex items-center justify-center size-10 shrink-0 rounded-xl bg-gradient-to-br from-[#E63946] to-[#D62839] text-white shadow-lg shadow-[#E63946]/20 transition-transform duration-300 group-hover:scale-105">
              <span className="material-symbols-outlined text-[24px]">hub</span>
            </div>
            {showExpanded && (
              <div className="min-w-0">
                <span className="block text-lg font-bold tracking-tight text-white leading-none group-hover:text-gray-100 transition-colors truncate">
                  ContactHQ
                </span>
                <span className="text-[10px] text-gray-500 font-medium tracking-wider uppercase">
                  Workspace
                </span>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => dispatch({ sideBar: false })}
            className="lg:hidden text-gray-400 hover:text-white transition-colors p-2 rounded-xl hover:bg-white/10 active:scale-95"
            aria-label="Close menu"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <nav className="relative z-10 flex-1 overflow-y-auto py-3 px-3 space-y-1 custom-scrollbar overscroll-contain">
          {navItems.map((item, index) => {
            const isActive =
              location.pathname === item.link ||
              (item.link !== "/dashboard" &&
                location.pathname.startsWith(item.link));

            return (
              <motion.button
                key={item.link}
                type="button"
                custom={index}
                initial="hidden"
                animate="visible"
                variants={itemVariants}
                onClick={() => handleListItemClick(item.link, item.title)}
                className={`group w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-sm font-medium transition-all duration-200 relative overflow-hidden active:scale-[0.98]
                  ${
                    isActive
                      ? "text-white shadow-lg shadow-[#E63946]/10"
                      : "text-gray-400 hover:bg-white/5 hover:text-white"
                  }
                `}
              >
                {isActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-[#E63946] to-[#B91C1C] opacity-90" />
                )}

                <span
                  className={`material-symbols-outlined relative z-10 text-[22px] transition-transform duration-300 
                  ${isActive ? "text-white" : "group-hover:scale-110"}`}
                  style={
                    isActive
                      ? { fontVariationSettings: "'FILL' 1" }
                      : undefined
                  }
                >
                  {item.icon}
                </span>

                {showExpanded && (
                  <span className="relative z-10 flex-1 text-left tracking-wide">
                    {item.title}
                  </span>
                )}
              </motion.button>
            );
          })}
        </nav>

        <div className="relative z-10 p-4 border-t border-white/5 bg-[#081522]/50 backdrop-blur-sm">
          <div
            className="flex items-center gap-3 mb-3 p-3 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors cursor-pointer active:scale-[0.98]"
            onClick={() => handleListItemClick("/myprofile", "My Profile")}
          >
            <div className="size-10 shrink-0 rounded-full bg-gradient-to-tr from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-sm shadow-inner overflow-hidden">
              {profilePicURL ? (
                <img
                  src={profilePicURL}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                userInitial
              )}
            </div>
            {showExpanded && (
              <>
                <div className="flex-1 overflow-hidden">
                  <p className="text-sm font-semibold text-white truncate">
                    {userName}
                  </p>
                  <p className="text-[11px] text-gray-400 truncate">
                    {userRole}
                  </p>
                </div>
                <span className="material-symbols-outlined text-gray-500 text-[18px]">
                  chevron_right
                </span>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={logoutHandler}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-xs font-semibold text-gray-400 hover:text-[#E63946] hover:bg-[#E63946]/10 transition-all duration-200 border border-transparent hover:border-[#E63946]/20 active:scale-[0.98]"
          >
            <span className="material-symbols-outlined text-[18px]">logout</span>
            {showExpanded && <span>Sign Out</span>}
          </button>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;
